import React, { useState } from "react";
import {
  MoreVertical,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  UserPlus,
  Loader2,
} from "lucide-react";

const ContactsFriendsTab = ({
  isCustomer = false,
  isPartner = false,
  contacts = [],
  isLoading = false,
  searchTerm = "",
  onOpenAddModal,
  onViewDetails,
  onEditContact,
  onDeleteContact,
}) => {
  const [activeMenuId, setActiveMenuId] = useState(null);

  const toggleMenu = (id, e) => {
    e.stopPropagation();
    setActiveMenuId((prev) => (prev === id ? null : id));
  };

  React.useEffect(() => {
    const handleOutside = () => setActiveMenuId(null);
    window.addEventListener("click", handleOutside);
    return () => window.removeEventListener("click", handleOutside);
  }, []);

  if (isLoading) {
    return (
      <div className="net-loading-wrap">
        <Loader2 size={36} className="net-spin" style={{ color: "#16a34a" }} />
        <p style={{ fontWeight: 600 }}>Loading friends and contacts...</p>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="net-empty">
        <div className="net-empty__icon-wrap" style={{ background: "#f0fdf4", color: "#16a34a" }}>
          <UserCheck size={32} />
        </div>
        <h3 className="net-empty__title">
          {searchTerm
            ? "No matching contacts found"
            : isCustomer
            ? "No friends yet"
            : isPartner
            ? "No clients yet"
            : "No Friends or Customers Yet"}
        </h3>
        <p className="net-empty__desc">
          {searchTerm
            ? `No contacts matched your search "${searchTerm}". Try a different name or number.`
            : isCustomer
            ? "Add friends and family here for quick communication and property sharing."
            : isPartner
            ? "Clients you add will appear here for quick contact and property sharing."
            : "Save your client list, family, and home-seeking friends here for quick direct communication and property sharing."}
        </p>
        <button
          type="button"
          className="net-btn net-btn--primary"
          style={{ background: "#16a34a" }}
          onClick={onOpenAddModal}
        >
          <UserPlus size={16} /> {isCustomer ? "Add a Friend" : isPartner ? "Add First Client" : "Add First Contact"}
        </button>
      </div>
    );
  }

  return (
    <div className="net-grid">
      {contacts.map((c) => {
        const id = c.id || c.contact_id;
        const name = `${c.first_name || c.firstName || ""} ${c.last_name || c.lastName || ""}`.trim() || "Friend / Contact";
        const mobile = c.mobile_number || c.mobileNo || "";
        const email = c.email_address || c.emailId || "";
        const area = c.area_name || c.area || "";
        const address = c.address || "";
        const cleanPhone = mobile.replace(/^91/, "");
        const whatsappUrl = cleanPhone ? `https://wa.me/91${cleanPhone}?text=Hi%20${encodeURIComponent(name)}` : null;

        return (
          <div key={id} className="net-card">
            <div>
              {/* Card Header */}
              <div className="net-card__header">
                <div className="net-card__profile" onClick={() => onViewDetails(c)} style={{ cursor: "pointer" }}>
                  <div
                    className="net-card__avatar"
                    style={{
                      background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                      color: "#15803d",
                      borderColor: "#bbf7d0",
                    }}
                  >
                    {name.charAt(0).toUpperCase()}
                  </div>
                  <div className="net-card__name-col">
                    <h4 className="net-card__name" title={name}>
                      {name}
                    </h4>
                    <span className="net-card__type-tag" style={{ color: "#16a34a" }}>
                      Personal Contact
                    </span>
                  </div>
                </div>

                {/* Context Menu */}
                <div className="net-menu-wrapper">
                  <button
                    type="button"
                    className="net-menu-trigger"
                    onClick={(e) => toggleMenu(id, e)}
                    title="Actions"
                  >
                    <MoreVertical size={16} />
                  </button>

                  {activeMenuId === id && (
                    <div className="net-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="net-dropdown-item"
                        onClick={() => {
                          setActiveMenuId(null);
                          onViewDetails(c);
                        }}
                      >
                        <Eye size={14} /> View Details
                      </button>
                      <button
                        type="button"
                        className="net-dropdown-item"
                        onClick={() => {
                          setActiveMenuId(null);
                          onEditContact(c);
                        }}
                      >
                        <Edit size={14} /> Edit Contact
                      </button>
                      <div className="net-dropdown-divider" />
                      <button
                        type="button"
                        className="net-dropdown-item net-dropdown-item--danger"
                        onClick={() => {
                          setActiveMenuId(null);
                          onDeleteContact(c);
                        }}
                      >
                        <Trash2 size={14} /> Delete Contact
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Details */}
              <div className="net-card__details">
                {mobile && (
                  <div className="net-card__detail-row">
                    <Phone size={13} className="net-card__detail-icon" />
                    <span>+91 {cleanPhone}</span>
                  </div>
                )}
                {email && (
                  <div className="net-card__detail-row">
                    <Mail size={13} className="net-card__detail-icon" />
                    <span>{email}</span>
                  </div>
                )}
                {(area || address) && (
                  <div className="net-card__detail-row">
                    <MapPin size={13} className="net-card__detail-icon" />
                    <span>{[area, address].filter(Boolean).join(", ")}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="net-card__actions-row">
              {mobile && (
                <a href={`tel:${mobile}`} className="net-quick-btn" title="Call">
                  <Phone size={13} /> Call
                </a>
              )}
              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="net-quick-btn net-quick-btn--wa"
                  title="WhatsApp"
                >
                  <MessageCircle size={13} /> WhatsApp
                </a>
              )}
              <button
                type="button"
                className="net-quick-btn"
                onClick={() => onViewDetails(c)}
                title="Details"
              >
                <Eye size={13} /> Info
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ContactsFriendsTab;
